import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

/** A spot on a demo screen (#808): its words and where it is, as % of the screenshot [x, y, width, height]. */
export interface Hotspot {
  title: string;
  text: string;
  r: [number, number, number, number];
}

interface Step {
  id: string;
  label: string;
  title: string;
  text: string;
}

interface Group {
  id: string;
  name: string;
  steps: string[];
}

/** Whether the page is phone-sized (#837): the demo switches to its phone layout there. */
export function usePhone(maxWidth = 640) {
  const query = `(max-width: ${maxWidth}px)`;
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches);
  useEffect(() => {
    const m = window.matchMedia?.(query);
    if (!m) return;
    const on = () => setPhone(m.matches);
    on();
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, [query]);
  return phone;
}

const NW = 1920;
const NH = 1200;
/** The least the phone demo zooms into a spot, so its words are big enough to read (#855). */
const MIN_ZOOM = 0.4;

/**
 * The demo on a phone (#837): one group picker and the screen chips, the screenshot zoomed into each spot with an
 * outline (tap it to see the whole screen), the spot's words below the picture instead of over it, and Back / Next
 * held at the bottom of the screen. Desktop and tablet keep the full tour.
 */
export function PhoneTour({
  groups,
  steps,
  index,
  go,
  spots,
}: {
  groups: Group[];
  steps: { group: Group; step: Step }[];
  index: number;
  go: (i: number) => void;
  spots: Hotspot[];
}) {
  const { group, step } = steps[index]!;
  const [spot, setSpot] = useState(0);
  const [whole, setWhole] = useState(false);
  const stage = useRef<HTMLButtonElement>(null);
  const chips = useRef<(HTMLButtonElement | null)[]>([]);
  const [width, setWidth] = useState(358);
  useEffect(() => {
    setSpot(0);
    setWhole(false);
  }, [index]);
  useEffect(() => {
    const el = stage.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth || 358));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    chips.current[group.steps.indexOf(step.id)]?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [group, step.id]);

  const current = !whole && spots.length ? spots[Math.min(spot, spots.length - 1)] : undefined;
  const height = Math.round(width * 0.7);
  // Zoom so the spot, with a little room around it, fills the frame; the whole screen when there's none.
  const box = (() => {
    if (!current) {
      const scale = width / NW;
      return { scale, left: 0, top: (height - NH * scale) / 2 };
    }
    const [x, y, w, h] = current.r;
    // Never smaller than the frame, and always close enough to read (#855): a spot nearly as wide as the screen would
    // otherwise show the whole screen shrunk down.
    const scale = Math.max(Math.min(width / ((w / 100) * NW * 1.25), height / ((h / 100) * NH * 1.25), 0.9), width / NW, height / NH, MIN_ZOOM);
    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
    // A card or list wider than the frame starts at its left edge, where its words start; a full-width bar stays centered.
    const wide = (w / 100) * NW * scale > width && x > 2;
    const cx = wide ? 12 - (x / 100) * NW * scale : width / 2 - ((x + w / 2) / 100) * NW * scale;
    return {
      scale,
      left: clamp(cx, width - NW * scale, 0),
      top: clamp(height / 2 - ((y + h / 2) / 100) * NH * scale, height - NH * scale, 0),
    };
  })();
  const at = whole ? -1 : Math.min(spot, spots.length - 1);
  const nextStep = steps[(index + 1) % steps.length]!.step;
  const lastSpot = at >= spots.length - 1;
  const next = () => {
    if (whole) setWhole(false);
    else if (!lastSpot) setSpot(spot + 1);
    else go(index + 1);
  };
  const back = () => {
    if (whole) setWhole(false);
    else if (at > 0) setSpot(spot - 1);
    else go(index - 1);
  };

  return (
    <div className="tour-phone">
      <label className="tour-phone-group">
        <span className="sr-only">Feature group</span>
        <select value={group.id} onChange={(e) => go(steps.findIndex((s) => s.group.id === e.target.value))}>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name} · {g.steps.length} screens
            </option>
          ))}
        </select>
      </label>
      <div className="tour-phone-chips" role="tablist" aria-label={group.name}>
        {group.steps.map((id, i) => {
          const s = steps.find((x) => x.step.id === id)!.step;
          return (
            <button
              key={id}
              ref={(el) => {
                chips.current[i] = el;
              }}
              type="button"
              role="tab"
              aria-selected={id === step.id}
              className={id === step.id ? 'active' : ''}
              onClick={() => go(steps.findIndex((x) => x.step.id === id))}
            >
              {s.label}
            </button>
          );
        })}
      </div>
      <div className="tour-phone-head">
        <h3>{step.title}</h3>
        <span className="muted small">
          {step.label}
          {spots.length > 0 && ` · ${spots.length} things to see`}
        </span>
      </div>
      <button
        ref={stage}
        type="button"
        className="tour-phone-stage"
        style={{ height }}
        onClick={() => spots.length && setWhole(!whole)}
        aria-label={whole ? 'Zoom back in' : 'See the whole screen'}
      >
        <img
          src={`/tour/${step.id}.jpg`}
          width={NW}
          height={NH}
          alt={`${step.label}: ${current ? current.title : step.title}`}
          style={{ width: NW * box.scale, height: NH * box.scale, left: box.left, top: box.top }}
        />
        {current && (
          <span
            className="tour-phone-ring"
            style={{
              left: box.left + (current.r[0] / 100) * NW * box.scale,
              top: box.top + (current.r[1] / 100) * NH * box.scale,
              width: (current.r[2] / 100) * NW * box.scale,
              height: (current.r[3] / 100) * NH * box.scale,
            }}
          />
        )}
      </button>
      {spots.length > 0 && <span className="muted small tour-phone-hint">{whole ? 'Tap the picture to zoom back in' : 'Tap the picture to see the whole screen'}</span>}
      <div className="tour-phone-note" role="status">
        {current ? (
          <>
            <span className="tour-phone-count">
              {at + 1} of {spots.length}
            </span>
            <strong>{current.title}</strong>
            <span>{current.text}</span>
          </>
        ) : (
          <span>{step.text}</span>
        )}
      </div>
      <div className="tour-phone-bar">
        <button type="button" className="btn" onClick={back}>
          <ChevronLeft size={16} /> Back
        </button>
        <span className="tour-phone-dots" aria-hidden="true">
          {spots.map((_, k) => (
            <span key={k} className={k === at ? 'on' : ''} />
          ))}
        </span>
        <button type="button" className="btn primary" onClick={next}>
          {lastSpot && !whole ? nextStep.label : 'Next'} <ChevronRight size={16} />
        </button>
      </div>
      <link rel="prefetch" href={`/tour/${nextStep.id}.jpg`} />
    </div>
  );
}
